import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Alert, Box, Chip, CircularProgress, Container, FormControlLabel, IconButton, Paper,
  Stack, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TableSortLabel, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import AssignmentReturnIcon from "@mui/icons-material/AssignmentReturn";

import { getFinancialPaymentRequests } from "../../services/backend/PaymentRequestLegalService";
import { PAYMENT_REQUEST_STATUS, PAYMENT_REQUEST_STATUS_UI, PERMISSIONS } from "../../constants";
import { PAYMENT_AREAS, getAreaOf, getStageOf } from "../contract/legal/payments/paymentRequestLogic";
import { formatReviewMoment } from "../contract/legal/payments/paymentReviewUi";

// Bandeja de Gestión de Pagos (legal-payment-financial-flow, D6). Lista las
// solicitudes de pago jurídicas en recorrido financiero, de TODOS los
// contratos: el alcance es por permiso de área, no por asignación contractual.
//
// Lo que pidió el acta #3 por fila: contrato, empresa, objeto, valor, % de
// ejecución, etapa actual y si fue devuelta. Lo que pidió el daily 2026-09-14:
// las fechas de emisión y vencimiento de la factura, ORDENABLES —Contabilidad
// ordena la causación por emisión, Tesorería los pagos por vencimiento.
//
// La etapa del actor viene preseleccionada y sus filas resaltadas; el filtro
// admite cualquier área para consultar. Sin correos ni campanita todavía (C11):
// esta bandeja ES el mecanismo de trabajo de cada área.

const currency = (value) =>
  "$" + (Number(value) || 0).toLocaleString("es-CO", { maximumFractionDigits: 0 });

const percent = (rate) => `${Math.round((Number(rate) || 0) * 100)}%`;

const formatCalendar = (iso) => {
  if (typeof iso !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return "—";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};

const ALL = "all";

// Claves ordenables → cómo leer el valor de la fila. Las fechas de calendario
// son ISO 'YYYY-MM-DD' y se comparan como texto; las ausentes van al final sin
// importar la dirección (una factura sin vencimiento no es "la más antigua").
const SORTERS = {
  invoiceIssuedAt: (r) => r.invoiceIssuedAt || null,
  invoiceDueAt: (r) => r.invoiceDueAt || null,
  invoiceAmount: (r) => Number(r.invoiceAmount) || 0,
  lastMovementAt: (r) => r.lastMovementAt || null,
  contractCode: (r) => r.contractCode || "",
};

const compare = (a, b, dir) => {
  if (a === null || a === undefined || a === "") return 1;
  if (b === null || b === undefined || b === "") return -1;
  const result = typeof a === "number" && typeof b === "number" ? a - b : String(a).localeCompare(String(b));
  return dir === "asc" ? result : -result;
};

const PaymentManagementList = () => {
  const navigate = useNavigate();
  const user = useSelector((state) => state.user.value);

  const [rows, setRows] = useState(null);
  const [includePaid, setIncludePaid] = useState(false);
  const [error, setError] = useState("");
  const [orderBy, setOrderBy] = useState("lastMovementAt");
  const [order, setOrder] = useState("desc");

  const permissions = user?.permissions || [];
  const isAdmin = !!user?.roles?.includes("ADMIN");

  // Áreas del actor: la primera es la preseleccionada. Quien porta varios roles
  // ve resaltadas todas las suyas.
  const myAreaKeys = useMemo(
    () => PAYMENT_AREAS.filter((a) => permissions.includes(a.permission)).map((a) => a.key),
    [permissions]
  );
  const [areaFilter, setAreaFilter] = useState(myAreaKeys[0] || ALL);

  useEffect(() => {
    if (!isAdmin && !permissions.includes(PERMISSIONS.PAYMENT_FLOW.LIST)) {
      navigate("/");
    }
  }, [isAdmin, permissions, navigate]);

  useEffect(() => {
    let active = true;
    setRows(null);
    (async () => {
      const result = await getFinancialPaymentRequests(user, { includePaid });
      if (!active) return;
      if (!result.ok) {
        setError("No fue posible cargar las solicitudes en recorrido financiero");
        setRows([]);
        return;
      }
      setRows(result.data || []);
    })();
    return () => { active = false; };
  }, [includePaid]); // eslint-disable-line

  const counts = useMemo(() => {
    const acc = { [ALL]: 0 };
    PAYMENT_AREAS.forEach((a) => { acc[a.key] = 0; });
    (rows || []).forEach((r) => {
      acc[ALL] += 1;
      const area = getAreaOf(r.status);
      if (area) acc[area.key] += 1;
    });
    return acc;
  }, [rows]);

  const visible = useMemo(() => {
    const list = (rows || []).filter((r) => areaFilter === ALL || getAreaOf(r.status)?.key === areaFilter);
    const read = SORTERS[orderBy] || SORTERS.lastMovementAt;
    return list.slice().sort((a, b) => compare(read(a), read(b), order));
  }, [rows, areaFilter, orderBy, order]);

  const handleSort = (key) => {
    if (orderBy === key) {
      setOrder(order === "asc" ? "desc" : "asc");
    } else {
      setOrderBy(key);
      setOrder(key === "invoiceAmount" || key === "lastMovementAt" ? "desc" : "asc");
    }
  };

  const sortLabel = (key, label) => (
    <TableSortLabel active={orderBy === key} direction={orderBy === key ? order : "asc"} onClick={() => handleSort(key)}>
      {label}
    </TableSortLabel>
  );

  return (
    <Container maxWidth="xl" sx={{ mt: 3, mb: 4 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" useFlexGap spacing={2}>
        <Box>
          <Typography variant="h5">Gestión de Pagos</Typography>
          <Typography variant="body2" color="text.secondary">
            Facturas de personas jurídicas aprobadas por supervisión, en su recorrido por
            Causación, Contabilidad, Presupuesto, Tesorería y Caja.
          </Typography>
        </Box>
        <FormControlLabel
          control={<Switch checked={includePaid} onChange={(e) => setIncludePaid(e.target.checked)} />}
          label="Incluir pagadas del año"
        />
      </Stack>

      <ToggleButtonGroup
        exclusive
        size="small"
        color="primary"
        value={areaFilter}
        onChange={(__, value) => value && setAreaFilter(value)}
        sx={{ mt: 2, flexWrap: "wrap" }}
      >
        <ToggleButton value={ALL}>Todas ({counts[ALL]})</ToggleButton>
        {PAYMENT_AREAS.map((a) => (
          <ToggleButton key={a.key} value={a.key} sx={myAreaKeys.includes(a.key) ? { fontWeight: 700 } : undefined}>
            {a.label} ({counts[a.key]})
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

      {rows === null ? (
        <Box sx={{ textAlign: "center", py: 6 }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{sortLabel("contractCode", "Contrato")}</TableCell>
                <TableCell>Empresa</TableCell>
                <TableCell>Objeto</TableCell>
                <TableCell>Factura</TableCell>
                <TableCell align="right">{sortLabel("invoiceAmount", "Valor")}</TableCell>
                <TableCell>{sortLabel("invoiceIssuedAt", "Emisión")}</TableCell>
                <TableCell>{sortLabel("invoiceDueAt", "Vencimiento")}</TableCell>
                <TableCell align="right">Ejecución</TableCell>
                <TableCell>Etapa</TableCell>
                <TableCell>{sortLabel("lastMovementAt", "Último movimiento")}</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.length === 0 && (
                <TableRow>
                  <TableCell colSpan={11}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
                      No hay solicitudes en esta etapa.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {visible.map((r) => {
                const statusUi = PAYMENT_REQUEST_STATUS_UI[r.status] || {};
                const stage = getStageOf(r.status);
                const mine = !!stage && permissions.includes(stage.permission);
                const returns = Array.isArray(r.financial?.returns) ? r.financial.returns : [];
                const lastEntry = r.financial?.history?.[r.financial.history.length - 1];
                const returnedNow = lastEntry?.action === "RETURN";
                const lastReturn = returns[returns.length - 1];
                return (
                  <TableRow key={r.requestId} hover selected={mine}>
                    <TableCell>
                      <Typography variant="body2">{r.contractCode}</Typography>
                      <Typography variant="caption" color="text.secondary">#{r.seq}</Typography>
                    </TableCell>
                    <TableCell>{r.businessName || "—"}</TableCell>
                    <TableCell sx={{ maxWidth: 260 }}>
                      <Tooltip title={r.contractSubject || ""}>
                        <Typography variant="body2" noWrap>{r.contractSubject || "—"}</Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{r.invoiceNumber || "—"}</TableCell>
                    <TableCell align="right">{r.invoiceAmount ? currency(r.invoiceAmount) : "—"}</TableCell>
                    <TableCell>{formatCalendar(r.invoiceIssuedAt)}</TableCell>
                    <TableCell>{formatCalendar(r.invoiceDueAt)}</TableCell>
                    <TableCell align="right">
                      <Tooltip
                        title={
                          r.execution
                            ? `Causado ${currency(r.execution.approved)} de ${currency(r.execution.budget)} · saldo ${currency(r.execution.balance)}`
                            : ""
                        }
                      >
                        <span>{r.execution ? percent(r.execution.executionRate) : "—"}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Chip
                          size="small"
                          label={statusUi.label || r.status}
                          color={statusUi.color}
                          sx={statusUi.sx}
                          icon={statusUi.Icon ? <statusUi.Icon /> : undefined}
                        />
                        {returns.length > 0 && (
                          <Tooltip
                            title={
                              lastReturn
                                ? `${returnedNow ? "Devuelta" : "Tuvo devolución"} desde ${lastReturn.fromLabel}${
                                  lastReturn.message ? `: ${lastReturn.message}` : ""
                                }`
                                : ""
                            }
                          >
                            <AssignmentReturnIcon fontSize="small" color={returnedNow ? "warning" : "disabled"} />
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{r.lastMovementAt ? formatReviewMoment(r.lastMovementAt) : "—"}</Typography>
                      {r.status === PAYMENT_REQUEST_STATUS.APPROVED && (
                        <Typography variant="caption" color="text.secondary">Pendiente de recepción</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={mine ? "Gestionar" : "Ver"}>
                        <IconButton size="small" onClick={() => navigate(`/payments/management/${r.requestId}`)}>
                          <DescriptionIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Container>
  );
};

export default PaymentManagementList;
